/**
 * Navigation & Background & Mobile Menu - Moon Moon Agency
 * Handles navigation, background filters, and mobile menu interactions
 */

/*********************************************************************************************
 * 🎯 NAVIGATION MODULE
 */
const NavigationManager = {
	_lastOpenedMegaLink: null,

	// Local configuration
	config: {
		selectors: {
			header: 'header',
			megaMenuLink: '[data-megamenulink]',
			menuLink: '[data-menulink]',
			filter: '[data-filter]',
			cartFilter: '[data-drawer-cart]',
			searchContainer: '[data-search-container]',
			headerBurger: '[data-header-burger]',
			menuMobile: '[data-menu-mobile]',
			menuSublinks: '[data-menu-sublinks]'
		},
		classes: {
			active: 'active',
			open: 'mm-open',
			drawerOpen: 'mm-drawer-open',
			stopScrolling: 'stop-scrolling',
			mobileMenuSlided: 'mm-mobile-menu-elem-slided'
		},
		timing: {
			filterHideDelay: 300
		}
	},

	/**
	 * Initialize navigation systems
	 */
	init() {
		this.initMegaMenus();
		this.initBackgroundFilter();
		this.initAccessibilityHandlers();
		this.initMegaMenuAccessibility();
		this.initMobileSubmenuAccessibility();
	},

	/**
	 * Initialize accessibility keyboard handlers for header buttons
	 */
	initAccessibilityHandlers() {
		// Burger menu button
		const burgerBtn = window.getElement('[data-header-burger]');
		if (burgerBtn) {
			burgerBtn.addEventListener('click', () => {
				window.mmMobileMenu();
				// aria-expanded géré dans MobileMenuManager.toggle()
			});
		}

		// Search toggle button
		const searchBtn = window.getElement('[data-search-toggle]');
		if (searchBtn) {
			searchBtn.addEventListener('click', () => {
				window.mmCloseMobileMenu();
				this.closeAllMegaMenus();
				window.mmShowSearch(searchBtn);
			});
		}

		// Mobile menu close button
		const menuCloseBtn = window.getElement('[data-menu-close]');
		if (menuCloseBtn) {
			menuCloseBtn.addEventListener('click', () => window.mmCloseMobileMenu());
		}

		// Cart toggle button
		const cartBtn = window.getElement('[data-cart-toggle]');
		if (cartBtn) {
			cartBtn.addEventListener('click', () => {
				window.mmOpenCart(cartBtn);
				window.mmCloseMobileMenu(true);
				window.mmHideSearch(true);
			});
		}

		// Global Escape key handler — goes back one submenu level before closing entirely
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const mobileMenu = window.getElement(this.config.selectors.menuMobile);
				if (!mobileMenu?.classList.contains(this.config.classes.open)) return;

				const activeTrigger = window.getElement('[data-mobile-submenu-trigger][aria-expanded="true"]');
				if (activeTrigger) {
					const selector = activeTrigger.dataset.mobileSubmenuTrigger;
					window.mmMobileMenuShowPrev(selector);
					activeTrigger.setAttribute('aria-expanded', 'false');
					activeTrigger.focus();
					window.MobileMenuManager.updateFocusTrap(mobileMenu);
				} else {
					window.mmCloseMobileMenu();
					burgerBtn?.focus();
				}
			}
		});

		window.log('info', 'NavigationManager', 'Accessibility handlers initialized');
	},

	/**
	 * Initialize mega menu and submenu positioning
	 */
	initMegaMenus() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) {
			window.log('warn', 'NavigationManager', 'Header not found');
			return;
		}

		// mega menus use CSS top: 100% (relative to nav)

		// Position simple dropdowns under their trigger link
		window.getElements(this.config.selectors.megaMenuLink).forEach((menuElem) => {
			const dropdown = menuElem.nextElementSibling;
			if (dropdown?.classList.contains('mm-header-simple-dropdown')) {
				dropdown.style.left = `${menuElem.offsetLeft}px`;
			}
		});

		// Position submenus (data-menulink with nested data-megamenu)
		const headerHeight = header.offsetHeight;
		window.getElements(this.config.selectors.menuLink).forEach((menuElem) => {
			const submenu = menuElem.querySelector('[data-megamenu]');
			if (submenu) {
				submenu.style.top = `${headerHeight - 1}px`;
			}
		});
	},

	/**
	 * Initialize mega menu keyboard accessibility
	 */
	initMegaMenuAccessibility() {
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const regularMenuLinks = window.getElements(this.config.selectors.menuLink);

		// Init: all mega menu panels start inert (not keyboard-accessible when closed)
		megaMenuLinks.forEach((link) => {
			const panel = link.nextElementSibling;
			if (panel?.hasAttribute('data-megamenu')) panel.setAttribute('inert', '');
		});

		// Mega menu links (with dropdown)
		megaMenuLinks.forEach((link) => {
			const megaMenu = link.nextElementSibling;
			if (!megaMenu?.hasAttribute('data-megamenu')) return;

			// Click to toggle mega menu
			link.addEventListener('click', (e) => {
				//e.preventDefault();
				this.toggleMegaMenu(link, megaMenu);
			});

			// Keyboard: Enter to toggle (with focus trap), ArrowDown to open + focus first
			link.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					this.toggleMegaMenu(link, megaMenu, true);
				} else if (e.key === 'ArrowDown') {
					e.preventDefault();
					const isOpen = link.getAttribute('aria-expanded') === 'true';
					if (!isOpen) {
						this.openMegaMenu(link, megaMenu, true);
					} else {
						this.setupMegaMenuFocusTrap(link, megaMenu);
					}
					const firstLink = megaMenu.querySelector('a');
					firstLink?.focus();
				}
			});

			// Hover to open (no focus trap — mouse users don't need it)
			link.addEventListener('mouseenter', () => {
				const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
				allMegaMenuLinks.forEach((otherLink) => {
					if (otherLink !== link) {
						const otherMegaMenu = otherLink.nextElementSibling;
						if (otherMegaMenu?.hasAttribute('data-megamenu')) {
							this.closeMegaMenu(otherLink, otherMegaMenu);
						}
					}
				});
				this.openMegaMenu(link, megaMenu);
			});
		});

		// Regular menu links and submenus
		regularMenuLinks.forEach((link) => {
			// Only add listener if it's NOT a mega menu link
			if (!link.hasAttribute('data-megamenulink')) {
				const submenu = link.querySelector('[data-megamenu]');

				if (submenu) {
					// This link HAS a submenu - close other mega menus on hover
					link.addEventListener('mouseenter', () => {
						this.closeAllMegaMenus();
						this.showBackgroundFilter();
					});

					// Hide background filter when leaving submenu
					link.addEventListener('mouseleave', () => {
						this.hideBackgroundFilter();
					});
				} else {
					// This link has NO dropdown - close all menus on hover
					link.addEventListener('mouseenter', () => {
						this.closeAllMegaMenus();
					});
				}
			}
		});

		// Mouse leave header to close all menus
		const header = window.getElement(this.config.selectors.header);
		if (header) {
			header.addEventListener('mouseleave', () => {
				this.closeAllMegaMenus();
			});
		}

		// Close mega menus when hovering the search button (if search not open)
		const searchBtn = window.getElement('[data-search-toggle]');
		if (searchBtn) {
			searchBtn.addEventListener('mouseenter', () => {
				const searchContainer = window.getElement(this.config.selectors.searchContainer);
				if (!searchContainer?.classList.contains(this.config.classes.open)) {
					this.closeAllMegaMenus();
				}
			});
		}

		// Escape key closes all mega menus and restores focus to trigger
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const hasOpenMenu = Array.from(megaMenuLinks).some(
					(link) => link.getAttribute('aria-expanded') === 'true'
				);
				if (hasOpenMenu) {
					const linkToRestore = this._lastOpenedMegaLink;
					this.closeAllMegaMenus();
					linkToRestore?.focus();
				}
			}
		});

		// Initialize mega menu product links hover/focus
		this.initMegaMenuProductLinks();

		window.log('info', 'NavigationManager', 'Mega menu accessibility initialized');
	},

	/**
	 * Initialize mega menu product links (replace inline onmouseenter)
	 */
	initMegaMenuProductLinks() {
		const productLinks = window.getElements('[data-mega-menu-products-link]');

		productLinks.forEach((link) => {
			const hideSelector = link.dataset.hideSelector;
			const showSelector = link.dataset.showSelector;

			// Hover event
			link.addEventListener('mouseenter', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});

			// Focus event (keyboard navigation)
			link.addEventListener('focus', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});
		});
	},

	/**
	 * Toggle mega menu state
	 */
	toggleMegaMenu(link, megaMenu, fromKeyboard = false) {
		const isOpen = link.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			this.closeMegaMenu(link, megaMenu);
		} else {
			this.closeAllMegaMenus();
			this.openMegaMenu(link, megaMenu, fromKeyboard);
		}
	},

	/**
	 * Open mega menu
	 */
	openMegaMenu(link, megaMenu, fromKeyboard = false) {
		link.setAttribute('aria-expanded', 'true');
		megaMenu.classList.add(this.config.classes.open);
		megaMenu.removeAttribute('inert');
		this._lastOpenedMegaLink = link;
		this.showBackgroundFilter();
		if (fromKeyboard) this.setupMegaMenuFocusTrap(link, megaMenu);
	},

	/**
	 * Close mega menu
	 */
	closeMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'false');
		megaMenu.classList.remove(this.config.classes.open);
		megaMenu.setAttribute('inert', '');
		this.removeMegaMenuFocusTrap(megaMenu);
	},

	/**
	 * Close all mega menus
	 */
	closeAllMegaMenus() {
		const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		allMegaMenuLinks.forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
			const megaMenu = link.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.classList.remove(this.config.classes.open);
				megaMenu.setAttribute('inert', '');
				this.removeMegaMenuFocusTrap(megaMenu);
			}
		});
		this.hideBackgroundFilter();
	},

	setupMegaMenuFocusTrap(link, megaMenu) {
		if (megaMenu._trapHandler) return;

		const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
		const focusables = Array.from(megaMenu.querySelectorAll(focusableSelector));
		if (!focusables.length) return;

		const first = focusables[0];
		const last = focusables[focusables.length - 1];

		const trapHandler = (e) => {
			if (e.key !== 'Tab') return;
			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault();
					this.closeMegaMenu(link, megaMenu);
					link.focus();
				}
			} else {
				if (document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};

		document.addEventListener('keydown', trapHandler);
		megaMenu._trapHandler = trapHandler;
	},

	removeMegaMenuFocusTrap(megaMenu) {
		if (megaMenu._trapHandler) {
			document.removeEventListener('keydown', megaMenu._trapHandler);
			delete megaMenu._trapHandler;
		}
	},

	/**
	 * Show specific mega menu products
	 */
	showMegaMenuProducts(classElemsToHide, classElemToShow) {
		try {
			window.getElements(classElemsToHide).forEach((elem) => (elem.style.display = 'none'));
			const elemToShow = window.getElement(classElemToShow);
			if (elemToShow) {
				elemToShow.style.display = 'grid';
			}
			window.emit('global:mega-menu-products-switched', { hideSelector: classElemsToHide, showSelector: classElemToShow });
		} catch (error) {
			window.log('error', 'NavigationManager', 'Failed to switch mega menu products', { error: error.message });
		}
	},

	/**
	 * Initialize background filter hover behaviors
	 */
	initBackgroundFilter() {
		const elements = {
			header: window.getElement(this.config.selectors.header),
			searchContainer: window.getElement(this.config.selectors.searchContainer),
			filter: window.getElement(this.config.selectors.filter),
			cart: window.getElement(this.config.selectors.cartFilter)
		};

		if (!elements.header || !elements.filter || !elements.cart) {
			window.log('warn', 'NavigationManager', 'Background filter elements not found');
			return;
		}

		const { header, searchContainer, filter, cart } = elements;
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const menuLinks = window.getElements(this.config.selectors.menuLink);

		const toggleFilter = (show) => {
			filter.classList.toggle(this.config.classes.active, show);
			window.emit(`global:background-filter-${show ? 'activated' : 'deactivated'}`, { 
				trigger: show ? 'toggle-on' : 'toggle-off',
				source: 'navigation' 
			});
		};

		// Event listeners
		megaMenuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(true))
		);
		
		menuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(false))
		);

		header.addEventListener('mouseleave', () => {
			const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
			const cartOpen = cart.classList.contains(this.config.classes.drawerOpen);
			
			if (!searchOpen && !cartOpen) {
				toggleFilter(false);
			}
		});

	},

	/**
	 * Show background filter
	 */
	showBackgroundFilter(zIndex) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) {
			window.log('warn', 'NavigationManager', 'Background filter element not found');
			return;
		}
		
		filter.classList.add(this.config.classes.active);
		if (zIndex) filter.style.zIndex = zIndex;
		window.emit('global:background-filter-activated', { 
			trigger: 'show', 
			zIndex, 
			hasZIndex: !!zIndex 
		});
	},

	/**
	 * Hide background filter
	 */
	hideBackgroundFilter() {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;
		
		setTimeout(() => filter.style.removeProperty('z-index'), this.config.timing.filterHideDelay);
		filter.classList.remove(this.config.classes.active);
		window.emit('global:background-filter-deactivated', { 
			trigger: 'hide',
			delay: this.config.timing.filterHideDelay 
		});
	},

	/**
	 * Initialize mobile submenu accessibility (replace inline onclick)
	 */
	initMobileSubmenuAccessibility() {
		// Init: mobile menu itself starts inert (closed on page load)
		const mobileMenuInit = window.getElement(this.config.selectors.menuMobile);
		if (mobileMenuInit) mobileMenuInit.setAttribute('inert', '');

		// Init: all subpanels start inert (not keyboard-accessible when closed)
		window.getElements('[data-mobile-submenu-trigger]').forEach(btn => {
			const panel = window.getElement(btn.dataset.mobileSubmenuTrigger);
			if (panel) panel.setAttribute('inert', '');
		});

		// Submenu trigger buttons (show next + move focus)
		const submenuTriggers = window.getElements('[data-mobile-submenu-trigger]');
		submenuTriggers.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuTrigger;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowNext(targetSelector);
				btn.setAttribute('aria-expanded', 'true');
				const panel = window.getElement(targetSelector);
				requestAnimationFrame(() => {
					window.MobileMenuManager.updateFocusTrap(panel);
					const firstFocusable = panel?.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
					firstFocusable?.focus();
				});
			});
		});

		// Submenu back buttons (show prev + restore focus to trigger)
		const submenuBackBtns = window.getElements('[data-mobile-submenu-back]');
		submenuBackBtns.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuBack;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowPrev(targetSelector);
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
				trigger?.focus();
				const mobileMenu = window.getElement(this.config.selectors.menuMobile);
				if (mobileMenu) window.MobileMenuManager.updateFocusTrap(mobileMenu);
			});
		});

		window.log('info', 'NavigationManager', 'Mobile submenu accessibility initialized');
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		this.initMegaMenus();
	}
};

/*********************************************************************************************
 * 📱 MOBILE MENU MODULE
 */
const MobileMenuManager = {
	// Reference to NavigationManager config
	config: NavigationManager.config,

	// Focus trap state
	focusTrap: {
		isActive: false,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null,
		triggerElement: null
	},

	/**
	 * Setup focus trap for mobile menu
	 */
	setupFocusTrap(mobileMenu) {
		// Store trigger element to restore focus on close
		this.focusTrap.triggerElement = document.activeElement;

		const focusableElements = Array.from(mobileMenu.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
		)).filter(el => !el.closest('[inert]'));

		if (focusableElements.length === 0) return;

		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab key to trap focus
		this.focusTrap.handleKeyDown = (e) => {
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		this.focusTrap.firstFocusable?.focus();

		window.log('info', 'MobileMenuManager', 'Focus trap activated');
	},

	/**
	 * Update focus trap bounds to a new container (e.g. when a subpanel opens/closes)
	 */
	updateFocusTrap(container) {
		if (!this.focusTrap.isActive) return;
		const focusables = Array.from(container.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
		)).filter(el => !el.closest('[inert]'));
		if (!focusables.length) return;
		this.focusTrap.firstFocusable = focusables[0];
		this.focusTrap.lastFocusable = focusables[focusables.length - 1];
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;

			// Restore focus to the element that triggered the menu
			if (this.focusTrap.triggerElement) {
				this.focusTrap.triggerElement.focus();
				this.focusTrap.triggerElement = null;
			}

			window.log('info', 'MobileMenuManager', 'Focus trap removed');
		}
	},

	/**
	 * Toggle mobile menu
	 */
	toggle() {
		if (window.innerWidth > window.MEDIUM_DESKTOP_BREAKPOINT) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const mobileMenuSecondary = window.getElement(this.config.selectors.menuSublinks);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found');
			return;
		}

		const headerHeight = header.offsetHeight;
		const headerMenuTop = header.getBoundingClientRect().top;
		const topPosition = `${headerHeight + headerMenuTop}px`;
		const heightCalc = `calc(var(--doc-height) - ${topPosition})`;

		[header, icon, mobileMenu].forEach((el) => el.classList.toggle(this.config.classes.open));

		Object.assign(mobileMenu.style, { top: '0', height: '100%' });
		if (mobileMenuSecondary) mobileMenuSecondary.style.height = heightCalc;

		const isOpen = mobileMenu.classList.contains(this.config.classes.open);

		// Update ARIA state
		icon.setAttribute('aria-expanded', String(isOpen));
		mobileMenu.setAttribute('aria-hidden', String(!isOpen));

		if (isOpen) {
			mobileMenu.removeAttribute('inert');
			html.classList.add(this.config.classes.stopScrolling);
			NavigationManager.showBackgroundFilter('var(--index-sm)');
			this.setupFocusTrap(mobileMenu);
			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, footer').forEach(el => el.inert = true);
		} else {
			mobileMenu.setAttribute('inert', '');
			html.classList.remove(this.config.classes.stopScrolling);
			NavigationManager.hideBackgroundFilter();
			this.removeFocusTrap();
			this._resetSubpanels();
			document.querySelectorAll('main[inert], footer[inert]').forEach(el => el.inert = false);
		}

		window.emit('global:mobile-menu-toggled', { headerHeight, topPosition });
	},

	/**
	 * Close mobile menu
	 */
	close(keepFilterActive = false) {
		if (window.innerWidth > window.MEDIUM_DESKTOP_BREAKPOINT) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found for closing');
			return;
		}

		// Early return : ne fait rien si le menu mobile n'etait pas ouvert.
		// Evite d'ecraser le scroll-lock d'autres overlays (cart drawer, etc.)
		if (!mobileMenu.classList.contains(this.config.classes.open)) return;

		[header, icon, mobileMenu].forEach((el) => el.classList.remove(this.config.classes.open));
		mobileMenu.setAttribute('inert', '');
		html.classList.remove(this.config.classes.stopScrolling);

		// Update burger aria-expanded
		icon.setAttribute('aria-expanded', 'false');

		// Restore background content + reset subpanels
		document.querySelectorAll('main[inert], footer[inert]').forEach(el => el.inert = false);
		this._resetSubpanels();

		// Remove focus trap when menu closes
		this.removeFocusTrap();

		if (!keepFilterActive) NavigationManager.hideBackgroundFilter();
		window.emit('global:mobile-menu-closed', { keepFilterActive });
	},

	/**
	 * Show next sub-menu
	 */
	showNext(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (element) {
			element.removeAttribute('inert');
			element.setAttribute('aria-hidden', 'false');
			element.classList.add(this.config.classes.mobileMenuSlided);
			window.emit('global:mobile-submenu-next', { selector: elemToShowClass });
		}
	},

	/**
	 * Show previous sub-menu
	 */
	showPrev(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (element) {
			element.classList.remove(this.config.classes.mobileMenuSlided);
			element.setAttribute('aria-hidden', 'true');
			element.setAttribute('inert', '');
			window.emit('global:mobile-submenu-prev', { selector: elemToShowClass });
		}
	},

	_resetSubpanels() {
		window.getElements('[data-mobile-submenu-trigger]').forEach(btn => {
			btn.setAttribute('aria-expanded', 'false');
			const panel = window.getElement(btn.dataset.mobileSubmenuTrigger);
			if (panel) {
				panel.classList.remove(this.config.classes.mobileMenuSlided);
				panel.setAttribute('aria-hidden', 'true');
				panel.setAttribute('inert', '');
			}
		});
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.NavigationManager = NavigationManager;
window.MobileMenuManager = MobileMenuManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Navigation functions
window.getMegaMenuPosition = () => NavigationManager.initMegaMenus();
window.mmMegaMenuShowProducts = (hide, show) => NavigationManager.showMegaMenuProducts(hide, show);

// Background filter functions
window.showBgFilter = (zIndex) => NavigationManager.showBackgroundFilter(zIndex);
window.hideBgFilter = () => NavigationManager.hideBackgroundFilter();

// Mobile menu functions
window.mmMobileMenu = () => MobileMenuManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileMenuManager.close(keep);
window.mmMobileMenuShowNext = (elem) => MobileMenuManager.showNext(elem);
window.mmMobileMenuShowPrev = (elem) => MobileMenuManager.showPrev(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	NavigationManager.init();
});

window.addEventListener('resize', window.debounce(() => {
	NavigationManager.onResize();
}, 150));